import { ArrowLeft, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

import { JourneyCanvas } from '../../components/layout/journey-canvas';
import { Button } from '../../components/ui/button';
import { BrandMark } from '../auth/auth-brand-mark';
import {
  DPA_EFFECTIVE_DATE_LABEL,
  DPA_VERSION,
  dpaSections,
  type DpaSection,
} from './dpa-content';

function MetaCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-[1.4rem] border border-[#eadfce] bg-white/85 p-4 shadow-[0_18px_38px_rgba(48,38,25,0.08)]">
      <p className="text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-[#8b7d6c]">
        {label}
      </p>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#16323b]">
        {value}
      </p>
    </div>
  );
}

function DocumentSection({ section }: { section: DpaSection }) {
  return (
    <section
      id={section.id}
      className="scroll-mt-24 border-t border-[#efe4d7] pt-8 first:border-t-0 first:pt-0"
    >
      <h2 className="font-serif text-[1.9rem] font-semibold tracking-tight text-[#16323b]">
        {section.title}
      </h2>

      {section.paragraphs?.map((paragraph) => (
        <p key={paragraph} className="mt-4 text-[0.98rem] leading-8 text-[#475569]">
          {paragraph}
        </p>
      ))}

      {section.bullets?.length ? (
        <ul className="mt-5 space-y-3">
          {section.bullets.map((bullet) => (
            <li
              key={bullet}
              className="flex gap-3 text-[0.98rem] leading-8 text-[#475569]"
            >
              <span className="mt-[0.9rem] h-2.5 w-2.5 flex-none rounded-full bg-[#0f766e]" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export function DpaPage() {
  return (
    <JourneyCanvas className="max-w-[96rem]">
      <div className="flex min-h-[calc(100vh-2rem)] flex-col gap-8 pb-10 pt-2">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <BrandMark />

          <Button
            asChild
            variant="ghost"
            className="rounded-full border border-[#e3d6c7] bg-[#fff7ef] text-[#16323b] hover:bg-[#f6ede3]"
          >
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar ao início
            </Link>
          </Button>
        </header>

        <section className="grid gap-6 xl:grid-cols-[19rem_minmax(0,1fr)]">
          <aside className="self-start xl:sticky xl:top-6">
            <div className="space-y-4 rounded-[2rem] border border-white/55 bg-white/74 p-5 shadow-[0_26px_60px_rgba(48,38,25,0.12)] backdrop-blur-xl">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-[1rem] bg-[#0f766e] text-white shadow-[0_18px_30px_rgba(15,118,110,0.24)]">
                  <FileText className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-[#9a5d2e]">
                    Página legal pública
                  </p>
                  <h1 className="mt-2 font-serif text-3xl font-semibold leading-tight text-[#16323b]">
                    Acordo de Tratamento de Dados
                  </h1>
                </div>
              </div>

              <p className="text-sm leading-7 text-[#556270]">
                Esta versão foi adaptada aos fluxos atuais da BelaHora para
                marcações, equipa, clientes e comunicações e foi pensada para ser
                referida a partir de contratos com clientes.
              </p>

              <div className="grid gap-3">
                <MetaCard label="Versão" value={DPA_VERSION} />
                <MetaCard label="Entrada em vigor" value={DPA_EFFECTIVE_DATE_LABEL} />
                <MetaCard
                  label="Integração"
                  value="Pensado para ser incorporado por remissão no contrato de serviços."
                />
              </div>

              <nav className="rounded-[1.4rem] border border-[#efe4d7] bg-[#fffaf4] p-4">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8b7d6c]">
                  Índice
                </p>
                <div className="mt-3 space-y-2 text-sm leading-6 text-[#556270]">
                  {dpaSections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="block rounded-xl px-3 py-2 transition hover:bg-white hover:text-[#16323b]"
                    >
                      {section.title}
                    </a>
                  ))}
                </div>
              </nav>
            </div>
          </aside>

          <article className="overflow-hidden rounded-[2.4rem] border border-white/55 bg-white/88 shadow-[0_32px_70px_rgba(48,38,25,0.12)] backdrop-blur-xl">
            <div className="border-b border-[#efe4d7] bg-[linear-gradient(140deg,rgba(22,50,59,0.98)_0%,rgba(15,79,75,0.96)_56%,rgba(15,118,110,0.95)_100%)] px-6 py-8 text-white sm:px-8 sm:py-10">
              <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#ffe6c5]">
                Modelo legal BelaHora
              </span>
              <h2 className="mt-4 max-w-4xl font-serif text-[clamp(2.5rem,5vw,4.1rem)] font-semibold leading-[0.98]">
                Acordo de Tratamento de Dados
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-8 text-[#d7e4e5] sm:text-lg">
                O presente DPA faz parte integrante do Contrato de Serviços
                celebrado entre o cliente identificado no contrato aplicável e{' '}
                <span className="font-semibold text-white">
                  Carlos Silveira, a operar sob a marca
                  BelaHora
                </span>
                .
              </p>
            </div>

            <div className="space-y-8 px-6 py-8 sm:px-8 sm:py-10">
              {dpaSections.map((section) => (
                <DocumentSection key={section.id} section={section} />
              ))}
            </div>
          </article>
        </section>
      </div>
    </JourneyCanvas>
  );
}
